import React from 'react'
import { useQuery } from '@tanstack/react-query'
import styled from '@emotion/styled'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { colors, radius, spacing } from '@mobi/component-library/Theme/Common'
import type { AvatarList, FollowMeProfile } from '@core/Areas/FollowMe/types'
import { ProfileAvatar } from '@core/Areas/FollowMe/Components/Common/ProfileAvatar'
import { getFollowMeAvatarList } from '@core/Areas/FollowMe/helpers/api'
import { unicodeToEmoji } from '@core/Areas/FollowMe/helpers/unicodeToEmojiConverter'
import { LoadingPlaceholder } from '@mobi/component-library/Common/LoadingPlaceholder'
import { trackBetLoopRandomiseAvatar } from '@core/Areas/FollowMe/helpers/analytics'

type Props = {
  profile: FollowMeProfile
  setEmoji: React.Dispatch<React.SetStateAction<string | null>>
  setColor: React.Dispatch<React.SetStateAction<string | null>>
  emoji: string | null
  color: string | null
}

export const EditAvatar: React.FC<Props> = ({ profile, setEmoji, setColor, emoji, color }) => {
  return (
    <EditAvatarComponent
      profile={profile}
      setEmoji={setEmoji}
      setColor={setColor}
      emoji={emoji}
      color={color}
    />
  )
}

// ===============
// Local Component
// ===============

const EditAvatarComponent: React.FC<Props> = ({ profile, setEmoji, setColor, emoji, color }) => {
  const [currentTab, setCurrentTab] = React.useState<'Emoji' | 'Background'>('Emoji')

  const { data: emojiList = [], isPending: isEmojiListPending } = useQuery({
    queryKey: ['follow-me', 'emoji-list'],
    queryFn: getFollowMeAvatarList,
    staleTime: Infinity,
    select: data => reorderAndFlattenCategories(data, preferredCategoryOrder),
  })

  const currentEmoji = emoji ?? profile?.avatar ?? ''
  const currentBgColor = color ?? profile?.color ?? ''

  const randomize = () => {
    if (emojiList.length === 0) return
    const randomEmoji = Math.floor(Math.random() * emojiList.length)
    const randomColor = Math.floor(Math.random() * colorOptions.length)

    setEmoji(emojiList[randomEmoji])
    setColor(colorOptions[randomColor])
    trackBetLoopRandomiseAvatar()
  }

  const selectOption = (choice: string) => {
    currentTab === 'Emoji' ? setEmoji(choice) : setColor(choice)
  }

  return (
    <EditAvatarWrapperStyled>
      <div>
        <span key={currentEmoji + currentBgColor}>
          <ProfileAvatar size='lg' color={currentBgColor} avatar={currentEmoji} />
        </span>

        <Button onClick={randomize} size='sm' color='secondary_color'>
          <Icon name='DuocolorDice6' />
          <span>Randomise</span>
        </Button>

        <Tabs>
          <TabButton
            content='Emoji'
            isSelected={currentTab === 'Emoji'}
            onClick={() => setCurrentTab('Emoji')}
          />
          <TabButton
            content='Background'
            isSelected={currentTab === 'Background'}
            onClick={() => setCurrentTab('Background')}
          />
        </Tabs>
      </div>

      <div
        key={currentTab}
        className={`edit-avatar__picker edit-avatar__picker--${currentTab.toLowerCase()}`}
      >
        {currentTab === 'Emoji'
          ? isEmojiListPending
            ? Array(40)
                .fill('')
                .map((_, i) => (
                  <LoadingPlaceholder
                    key={i}
                    height='4.6rem'
                    width='4.6rem'
                    borderRadius={radius.full}
                  />
                ))
            : emojiList.map((emoji, i) => (
                <button
                  key={i}
                  type='button'
                  className={currentEmoji === emoji ? 'edit-avatar__emoji--selected' : ''}
                  onClick={() => selectOption(emoji)}
                >
                  {unicodeToEmoji(emoji)}
                </button>
              ))
          : colorOptions.map((gradient, i) => (
              <button
                key={i}
                type='button'
                style={{ background: gradient }}
                className={currentBgColor === gradient ? 'edit-avatar__bg--selected' : ''}
                onClick={() => selectOption(gradient)}
              />
            ))}
      </div>
    </EditAvatarWrapperStyled>
  )
}

// =======
// Helpers
// =======

const colorOptions = [
  'linear-gradient(180deg, #F7F8EC 0%, #A0E529 100%)',
  'linear-gradient(180deg, #CCDDF9 0%, #0962FA 100%)',
  'linear-gradient(180deg, #F9E2CD 0%, #FB8D08 100%)',
  'linear-gradient(180deg, #F6DDF5 0%, #DD9EC3 100%)',
  'linear-gradient(180deg, #F6EFFC 0%, #E0BBFF 100%)',
  'linear-gradient(180deg, #D8FFD6 0%, #36E56B 100%)',
  'linear-gradient(180deg, #CCF3F8 0%, #30C0C0 100%)',
  'linear-gradient(180deg, #F9CCCD 0%, #DB3336 100%)',
  'linear-gradient(180deg, #E5CCF9 0%, #6F0BFB 100%)',
  'linear-gradient(180deg, #FBFFE7 0%, #F2FF00 100%)',
  'linear-gradient(180deg, #E1FFF2 0%, #13DA77 100%)',
  'linear-gradient(180deg, #FFE0CB 0%, #FB5B0B 100%)',
  'linear-gradient(180deg, #DEF5FD 0%, #4FCFF2 100%)',
] as const

function reorderAndFlattenCategories(categories: AvatarList[], preferredOrder: string[]): string[] {
  const categoryMap = new Map(categories.map(cat => [cat.category, cat]))

  const orderedCategories: AvatarList[] = [
    ...(preferredOrder.map(name => categoryMap.get(name)).filter(Boolean) as AvatarList[]),
    ...categories.filter(cat => !preferredOrder.includes(cat.category)),
  ]

  return orderedCategories.flatMap(cat => cat.avatars)
}

const preferredCategoryOrder = [
  'Smileys',
  'Gestures & Body Parts',
  'People & Fantasy',
  'Animals & Nature',
  'Food & Drink',
]

// ======
// Styles
// ======

const EditAvatarWrapperStyled = styled.div({
  '@keyframes animate-pop': {
    from: { opacity: 0.3, transform: 'scale(0.8, 0.8)' },
    to: { opacity: 1, transform: 'scale(1, 1)' },
  },

  '> div:first-of-type': {
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    backgroundColor: colors.white,
    zIndex: 1,
    gap: spacing.sm,

    '> button': { alignSelf: 'center' },

    '> span:first-of-type': {
      alignSelf: 'center',
      animationDuration: '0.2s',
      animationName: 'animate-pop',
      animationTimingFunction: 'cubic-bezier(.26, .53, .74, 1.2)',
    },
  },

  '.edit-avatar__picker': {
    display: 'flex',
    minHeight: 0,
    gap: spacing.md,
    alignSelf: 'stretch',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    scrollBehavior: 'smooth',
    overscrollBehavior: 'contain',
    justifyContent: 'space-between',
    paddingTop: spacing.smx1,
    paddingInline: spacing.smx2,

    '> button': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      background: colors.white,
      borderRadius: radius.full,
      boxSizing: 'border-box',
      userSelect: 'none',
      cursor: 'pointer',
      padding: 0,
      border: 0,
      WebkitTapHighlightColor: 'transparent',
      transition: 'all 0.25s ease-out',

      ':active': { transform: 'scale(0.9)', opacity: 0.75 },
    },

    '&--emoji': {
      gap: spacing.big,

      '> button': {
        outline: '0.2rem solid transparent',
        width: '4.6rem',
        height: '4.6rem',
        lineHeight: 1,
        textAlign: 'center',
        fontSize: '3.8rem',

        '&.edit-avatar__emoji--selected': {
          outlineColor: colors.studio[600],
        },
      },
    },

    '&--background': {
      '> button': {
        width: '10rem',
        height: '10rem',
        border: `${spacing.smx2} solid white`,
        outline: `${spacing.smx2} solid transparent`,

        '&.edit-avatar__bg--selected': {
          outlineColor: colors.studio[600],
        },
      },
    },
  },
})
